import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query';

import { useServiceError } from '@/hooks';
import {
  createSubArea,
  deleteSubArea,
  subAreaByAreaId,
  updateSubArea,
} from './SubAreas.service';
import {
  CreateSubAreaRequest,
  ListSubAreasQuery,
  UpdateSubAreaRequest,
} from './SubAreas.types';

import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import { notification } from 'antd';
import { useIntl } from 'react-intl';
import notificationError from '@/utils/functions/notificationError';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';

export function useDeleteSubArea() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation((id: number) => deleteSubArea(id), {
    onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'areas.remove.notification.success',
        }),
        description: 'Sub-área removida com sucesso!',
      }),
        queryClient.invalidateQueries(['subAreas']);
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.includes('Sub Area cannot be deleted due to relation.')
              ? 'Não é possível excluir a área, pois possui vinculos com outros módulos.'
              : err.includes('SubArea already has a link with another entity:')
              ? 'Não é possível excluir a área, pois possui vinculos com outros módulos.'
              : err,
          ),
      });
    },
  });

  return mutation;
}

export function useSubAreaByAreaId(params: ListSubAreasQuery) {
  const query = useInfiniteQuery(
    ['subAreas', { ...params }],
    ({ pageParam }) => {
      const req: ListSubAreasQuery = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };
      return subAreaByAreaId(req);
    },
    {
      enabled: hasPermissions([Authorities.REGISTER_SUBAREA_VIEW]),
      getNextPageParam: (lastPage) => lastPage.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useCreateSubArea() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: CreateSubAreaRequest) => createSubArea(obj),
    {
      onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'areas.subareas.create.notification.success',
          }),
          description: intl.formatMessage({
            id: 'areas.subareas.create.notification.successDescription',
          }),
        });
        queryClient.invalidateQueries(['subAreas']);
      },

      onError(error) {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) =>
              err.includes('Already a Sub Area with the name')
                ? 'Já existe uma sub-área cadastrada com essa descrição.'
                : err,
            ),
        });
      },
    },
  );

  return mutation;
}

export function useUpdateSubArea() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: UpdateSubAreaRequest) => updateSubArea(obj),
    {
      onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'areas.subareas.update.notification.success',
          }),
          description: intl.formatMessage({
            id: 'areas.subareas.update.notification.successDescription',
          }),
        });

        queryClient.invalidateQueries(['subAreas']);
      },

      onError(error) {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) =>
              err.includes('Already a Sub Area with the name')
                ? 'Já existe uma sub-área cadastrada com essa descrição.'
                : err.includes('Already a Group with the name')
                ? 'Já existe uma sub-área cadastrada com essa descrição.'
                : err,
            ),
        });
      },
    },
  );

  return mutation;
}
